import { useEffect, useState } from 'react'
import { Loader2, Scissors, Sparkles, Store, UserRound } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { fetchCompany, updateCompany } from '@/lib/api/company'
import { fetchServicesPage } from '@/lib/api/services'
import { createServiceCategory, fetchServiceCategories } from '@/lib/api/serviceCategories'
import { createService } from '@/lib/api/services'
import { createStylist, fetchStylistsPage } from '@/lib/api/stylists'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { setOnboardingState, shouldPromptOnboarding } from '@/lib/onboarding'
import { SALON_DESK_UI } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

const DEFAULT_SERVICES = [
  { name: 'Haircut', price: '80' },
  { name: 'Blow dry', price: '60' },
  { name: 'Manicure', price: '50' },
]

/**
 * @param {{ onComplete: () => void, onSkip: () => void }} props
 */
export function OnboardingWizard({ onComplete, onSkip }) {
  const { user, refreshUser } = useAuth()
  const { activeBranch } = useBranch()
  const [step, setStep] = useState(0)
  const [salonName, setSalonName] = useState('')
  const [services, setServices] = useState(DEFAULT_SERVICES)
  const [staffName, setStaffName] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      try {
        const company = await fetchCompany()
        if (!active) return
        setSalonName(company.name ?? '')
      } catch {
        if (!active) return
        setSalonName(activeBranch?.name ?? '')
      } finally {
        if (active) setLoading(false)
      }
    }

    load()
    return () => {
      active = false
    }
  }, [activeBranch?.name])

  async function handleSalonStep(event) {
    event.preventDefault()
    if (!salonName.trim()) {
      setError('Please enter your salon name.')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      await updateCompany({ name: salonName.trim() })
      await refreshUser()
      setStep(1)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save salon name.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function ensureDefaultCategory() {
    const categories = await fetchServiceCategories()
    if (categories.length > 0) {
      return categories[0].id
    }

    const created = await createServiceCategory({
      name: 'Services',
      sort: 10,
      is_active: true,
      bookable_online_default: true,
    })
    return created.id
  }

  async function handleServicesStep(event) {
    event.preventDefault()
    const rows = services.filter((row) => row.name.trim())
    if (rows.length < 1) {
      setError('Add at least one service with a name and price.')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      const categoryId = await ensureDefaultCategory()
      for (const row of rows.slice(0, 3)) {
        const price = Number(row.price)
        await createService({
          name: row.name.trim(),
          service_category_id: categoryId,
          duration: 30,
          price_ladies: Number.isFinite(price) ? price : 0,
          price_gents: Number.isFinite(price) ? price : 0,
          is_active: true,
          bookable_online: true,
        })
      }
      setStep(2)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save services.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleStaffStep(event) {
    event.preventDefault()
    if (!staffName.trim()) {
      setError('Please enter a staff member’s name.')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      await createStylist({
        name: staffName.trim(),
        is_active: true,
        bookable_online: true,
        skills: [],
      })
      if (user?.id) {
        setOnboardingState(user.id, 'done')
      }
      onComplete()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add staff member.'))
    } finally {
      setSubmitting(false)
    }
  }

  function handleSkip() {
    if (user?.id) {
      setOnboardingState(user.id, 'skipped')
    }
    onSkip()
  }

  const steps = [
    { title: 'Salon name', icon: Store },
    { title: 'Services', icon: Scissors },
    { title: 'Staff', icon: UserRound },
  ]

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <div
        className="salon-panel w-full max-w-lg shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        <div className="mb-6 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 id="onboarding-title" className="text-xl font-semibold tracking-tight">
              Welcome to Salon Desk
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Three quick steps so your team can book and take payments today.
            </p>
          </div>
        </div>

        <ol className="mb-6 flex gap-2">
          {steps.map((item, index) => {
            const Icon = item.icon
            return (
              <li
                key={item.title}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-xs font-medium',
                  index === step
                    ? 'border-primary/30 bg-primary/5 text-primary'
                    : index < step
                      ? 'border-border/60 bg-muted/30 text-muted-foreground'
                      : 'border-border/40 text-muted-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{item.title}</span>
              </li>
            )
          })}
        </ol>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            <span>Loading…</span>
          </div>
        ) : (
          <>
            {error ? (
              <p className="mb-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            {step === 0 ? (
              <form onSubmit={handleSalonStep} className="space-y-4">
                <label className="block space-y-2">
                  <span className="text-sm font-medium">What is your salon called?</span>
                  <input
                    type="text"
                    value={salonName}
                    onChange={(event) => setSalonName(event.target.value)}
                    className="h-12 w-full rounded-xl border border-input bg-background px-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="e.g. Lumière Ladies Spa"
                    autoFocus
                  />
                </label>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="submit" size="lg" className="rounded-2xl" disabled={submitting}>
                    {submitting ? 'Saving…' : 'Continue'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={handleSkip}>
                    Skip for now
                  </Button>
                </div>
              </form>
            ) : null}

            {step === 1 ? (
              <form onSubmit={handleServicesStep} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Add up to three services with one price each. You can change these later in Services.
                </p>
                <ul className="space-y-3">
                  {services.map((row, index) => (
                    <li key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(event) => {
                          const next = [...services]
                          next[index] = { ...next[index], name: event.target.value }
                          setServices(next)
                        }}
                        placeholder="Service name"
                        className="h-11 min-w-0 flex-1 rounded-xl border border-input bg-background px-3 text-sm"
                      />
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={row.price}
                        onChange={(event) => {
                          const next = [...services]
                          next[index] = { ...next[index], price: event.target.value }
                          setServices(next)
                        }}
                        placeholder="AED"
                        className="h-11 w-24 rounded-xl border border-input bg-background px-3 text-sm"
                        aria-label={`Price for ${row.name || 'service'}`}
                      />
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="submit" size="lg" className="rounded-2xl" disabled={submitting}>
                    {submitting ? 'Saving…' : 'Continue'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setStep(0)}>
                    Back
                  </Button>
                </div>
              </form>
            ) : null}

            {step === 2 ? (
              <form onSubmit={handleStaffStep} className="space-y-4">
                <label className="block space-y-2">
                  <span className="text-sm font-medium">Who is on the team?</span>
                  <input
                    type="text"
                    value={staffName}
                    onChange={(event) => setStaffName(event.target.value)}
                    className="h-12 w-full rounded-xl border border-input bg-background px-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="e.g. Sara"
                    autoFocus
                  />
                </label>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="submit" size="lg" className="rounded-2xl" disabled={submitting}>
                    {submitting ? 'Finishing…' : 'Finish setup'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                    Back
                  </Button>
                </div>
              </form>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Loads counts and decides whether to show onboarding for owners.
 */
export function OnboardingGate({ children }) {
  const { user } = useAuth()
  const [checking, setChecking] = useState(true)
  const [showWizard, setShowWizard] = useState(false)

  useEffect(() => {
    let active = true

    async function evaluate() {
      if (!SALON_DESK_UI.showOnboardingWizard || !user?.id) {
        if (active) {
          setChecking(false)
          setShowWizard(false)
        }
        return
      }

      try {
        const [services, staff] = await Promise.all([
          fetchServicesPage({ per_page: 1 }),
          fetchStylistsPage({ per_page: 1 }),
        ])
        if (!active) return
        const prompt = shouldPromptOnboarding(user, {
          serviceCount: services.meta.total ?? services.items.length,
          staffCount: staff.meta.total ?? staff.items.length,
        })
        setShowWizard(prompt)
      } catch {
        if (active) setShowWizard(false)
      } finally {
        if (active) setChecking(false)
      }
    }

    setChecking(true)
    evaluate()
    return () => {
      active = false
    }
  }, [user])

  if (checking) {
    return children
  }

  return (
    <>
      {children}
      {showWizard ? (
        <OnboardingWizard
          onComplete={() => setShowWizard(false)}
          onSkip={() => setShowWizard(false)}
        />
      ) : null}
    </>
  )
}
